/**
 * AG Grid column definitions for Provisions list view.
 * Modernized for Angular 20+ - no moment.js or lodash dependencies.
 */

import { dateValueFormatter as dateFormatter } from '../utils/format-date-cell';

// Yes/No formatter for boolean fields
function yesNoFormatter(params: any): string {
  return params.data?.actionCompleted ? 'Yes' : 'No';
}

// Strip HTML tags and return plain text
function stripHtml(html: string): string {
  if (!html) return '';
  // Create a temporary element to parse HTML and extract text
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

// Truncate text to a max length with ellipsis
function truncateText(text: string, maxLength: number = 150): string {
  if (!text) return '';
  const plainText = text.trim();
  if (plainText.length <= maxLength) return plainText;
  return plainText.substring(0, maxLength).trim() + '...';
}

// Value getter that strips HTML and truncates for cell display
function descriptionValueGetter(params: any): string {
  const html = params.data?.description;
  if (!html) return '';
  const plainText = stripHtml(html);
  return truncateText(plainText, 150);
}

// Tooltip value getter - shows more content (but still plain text for performance)
function descriptionTooltipGetter(params: any): string {
  const html = params.data?.description;
  if (!html) return '';
  const plainText = stripHtml(html);
  // Show more in tooltip, but still truncate very long content
  return truncateText(plainText, 500);
}

// Link renderer for source record links
function linkRenderer(params: any): HTMLElement | string {
  if (params.value) {
    const a = document.createElement('a');
    a.innerText = 'View Source';
    a.href = params.value;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.addEventListener('click', event => event.stopPropagation());
    return a;
  }
  return '';
}

// Column definitions
export const colProvNum = {
  colId: 'provNum',
  headerName: 'Provision ID',
  field: 'provNum',
  width: 120,
  filter: 'agTextColumnFilter'
};

export const colName = {
  colId: 'name',
  headerName: 'Name',
  field: 'provName',
  width: 200,
  filter: 'agTextColumnFilter',
  autoHeight: true
};

export const colDescription = {
  colId: 'description',
  headerName: 'Provision Language',
  field: 'description',
  width: 300,
  // Use valueGetter to strip HTML and truncate for performance
  valueGetter: descriptionValueGetter,
  // Show more content in tooltip on hover
  tooltipValueGetter: descriptionTooltipGetter,
  autoHeight: false,
  filter: 'agTextColumnFilter'
};

export const colOwner = {
  colId: 'owner',
  headerName: 'Owner',
  field: 'owner.friendlyName',
  width: 150,
  filter: 'agTextColumnFilter',
  valueGetter: (params: any) => {
    const owner = params.data?.owner;
    if (!owner) return '';
    if (typeof owner === 'string') return owner;
    return owner.friendlyName || owner.email || '';
  }
};

export const colImportantDate = {
  colId: 'importantDate',
  headerName: 'Critical Date',
  field: 'criticalDate',
  width: 130,
  valueFormatter: dateFormatter,
  filter: 'agDateColumnFilter'
};

export const colRequiredAction = {
  colId: 'requiredAction',
  headerName: 'Required Action',
  field: 'requiredAction',
  width: 200,
  filter: 'agTextColumnFilter'
};

export const colRiskDomain = {
  colId: 'riskDomain',
  headerName: 'Risk Domain',
  field: 'riskDomain',
  width: 130,
  filter: 'agTextColumnFilter'
};

export const colRiskName = {
  colId: 'riskName',
  headerName: 'Risk Name',
  field: 'riskName',
  width: 150,
  filter: 'agTextColumnFilter'
};

export const colLikelihood = {
  colId: 'likelihood',
  headerName: 'Likelihood',
  field: 'likelihoodScore',
  width: 110,
  filter: 'agTextColumnFilter'
};

export const colImpact = {
  colId: 'impact',
  headerName: 'Impact',
  field: 'impactScore',
  width: 100,
  filter: 'agTextColumnFilter'
};

export const colSourceRecord = {
  colId: 'sourceRecord',
  headerName: 'Source Record',
  field: 'contractFriendlyName',
  width: 180,
  filter: 'agTextColumnFilter',
  valueGetter: (params: any) => {
    return params.data?.contractFriendlyName || params.data?.sourceName || '';
  }
};

export const colSourceType = {
  colId: 'sourceType',
  headerName: 'Source Type',
  field: 'sourceType',
  width: 120,
  filter: 'agTextColumnFilter'
};

export const colSourceId = {
  colId: 'sourceId',
  headerName: 'Source ID',
  field: 'sourceId',
  width: 120,
  filter: 'agTextColumnFilter'
};

export const colContractNum = {
  colId: 'contractNum',
  headerName: 'Contract Number',
  field: 'contractNum',
  width: 140,
  filter: 'agTextColumnFilter'
};

export const colLinkToSource = {
  colId: 'linkToSource',
  headerName: 'Link To Source',
  field: 'linkToSource',
  width: 130,
  cellRenderer: linkRenderer,
  filter: false,
  sortable: false
};

export const colActionCompleted = {
  colId: 'actionCompleted',
  headerName: 'Action Completed',
  field: 'actionCompleted',
  width: 140,
  valueFormatter: yesNoFormatter,
  filter: 'agTextColumnFilter'
};

export const colActionDate = {
  colId: 'actionDate',
  headerName: 'Action Date',
  field: 'actionDate',
  width: 130,
  valueFormatter: dateFormatter,
  filter: 'agDateColumnFilter'
};

export const colActionSummary = {
  colId: 'actionSummary',
  headerName: 'Action Summary',
  field: 'actionSummary',
  width: 200,
  filter: 'agTextColumnFilter'
};

export const colApprovedProvision = {
  colId: 'approvedProvision',
  headerName: 'Approved',
  field: 'approvedProvision',
  width: 100,
  valueFormatter: (params: any) => params.value ? 'Yes' : 'No',
  filter: 'agTextColumnFilter'
};

export const colApprover = {
  colId: 'approver',
  headerName: 'Approver',
  field: 'approver',
  width: 150,
  filter: 'agTextColumnFilter',
  valueGetter: (params: any) => {
    const approver = params.data?.approver;
    if (!approver) return '';
    if (typeof approver === 'string') return approver;
    return approver.friendlyName || approver.email || '';
  }
};

export const colContractTypes = {
  colId: 'contractTypes',
  headerName: 'Contract Types',
  field: 'contractTypes',
  width: 180,
  filter: 'agTextColumnFilter',
  valueGetter: (params: any) => {
    const types = params.data?.contractTypes;
    if (!types || !Array.isArray(types)) return '';
    return types.join(', ');
  }
};

export const colRiskCategory = {
  colId: 'riskCategory',
  headerName: 'Risk Category',
  field: 'riskCategory',
  width: 130,
  filter: 'agTextColumnFilter'
};

export const colKeyPhrases = {
  colId: 'keyPhrases',
  headerName: 'Key Phrases',
  field: 'keyPhrases',
  width: 200,
  filter: 'agTextColumnFilter',
  valueGetter: (params: any) => {
    const phrases = params.data?.keyPhrases;
    if (!phrases || !Array.isArray(phrases)) return '';
    return phrases.join(', ');
  }
};

export const colCommentsRecommendations = {
  colId: 'commentsRecommendations',
  headerName: 'Comments/Recommendations',
  field: 'commentsRecommendations',
  width: 250,
  filter: 'agTextColumnFilter',
  // Strip HTML and truncate for performance
  valueGetter: (params: any) => {
    const html = params.data?.commentsRecommendations;
    if (!html) return '';
    return truncateText(stripHtml(html), 150);
  },
  tooltipValueGetter: (params: any) => {
    const html = params.data?.commentsRecommendations;
    if (!html) return '';
    return truncateText(stripHtml(html), 500);
  }
};

export const colAiGenerated = {
  colId: 'aiGenerated',
  headerName: 'AI Generated',
  field: 'aiGenerated',
  width: 120,
  valueFormatter: (params: any) => params.value ? 'Yes' : 'No',
  filter: 'agTextColumnFilter'
};

export const colAiSectionKey = {
  colId: 'aiSectionKey',
  headerName: 'Section Type',
  field: 'aiSectionKey',
  width: 140,
  filter: 'agTextColumnFilter'
};

// All columns array for column customization
export const allColumns = [
  colProvNum,
  colName,
  colDescription,
  colOwner,
  colApprover,
  colContractTypes,
  colRiskCategory,
  colImportantDate,
  colRequiredAction,
  colRiskDomain,
  colRiskName,
  colLikelihood,
  colImpact,
  colSourceRecord,
  colSourceType,
  colSourceId,
  colContractNum,
  colLinkToSource,
  colActionCompleted,
  colActionDate,
  colActionSummary,
  colApprovedProvision,
  colKeyPhrases,
  colCommentsRecommendations,
  colAiGenerated,
  colAiSectionKey
];

// Column select list for UI dropdowns
export const columnSelectList = allColumns.map(col => ({
  value: col,
  viewValue: col.headerName
}));
